import React from 'react';
import styled from 'styled-components';

import { Icon } from '@ui/icons/Icon.tsx';
import { getThemeVal } from '@utils/styled-component-tools.ts';

import { PrimaryLabel, SecondaryLabel } from '../styles.ts';

const DivAsCheckbox = styled.div`
  position: relative;
  height: 52px;
  font-size: ${getThemeVal('fonts.size.h2', '18px')};
  font-weight: ${getThemeVal('fontWeights.normal', '500')};
  color: ${getThemeVal('colors.primaryButton', '#2266DD')};
  overflow: hidden;
  border: 1px solid ${getThemeVal('colors.primaryButton', '#2266DD')};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  padding-block: 4px;
  padding-inline: 4px 18px;
  border-radius: ${getThemeVal('radii.lg')};
  &[aria-checked='true'] {
    background-color: var(--color-primaryButton-10);
  }
  &:focus-visible {
    background-color: var(--color-primaryButton-10);
    outline: 1px solid ${getThemeVal('colors.primaryButton', '#2266DD')};
  }
  &:hover {
    cursor: pointer;
    background-color: var(--color-primaryButton-10);
  }

  // media query for high zoom widths on mobile devices
  @media (max-width: ${props => `${props.$highZoomMaxWidth ?? 320}px`}) {
    flex-direction: column;
    height: 100%;
  }
`;

const CheckIconWrapper = styled.div`
  position: absolute;
  height: 20px;
  width: 20px;
  inset-inline-start: 16px;
  overflow: hidden;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  top: 0;
  bottom: 0;
  margin: auto;
  border: 1px solid ${getThemeVal('colors.secondary')};
  background-color: ${({ $selected }) =>
    $selected ? getThemeVal('colors.primaryButton') : getThemeVal('colors.widgetBackground')};
  & svg {
    fill: white;
  }

  // media query for high zoom widths on mobile devices
  @media (max-width: ${props => `${props.$highZoomMaxWidth ?? 320}px`}) {
    position: unset;
  }
`;

const ImageIconWrapper = styled.div`
  height: 40px;
  width: 40px;
  margin-inline-start: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: none;
`;

const StyledCheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const LaneTypeArea = ({ default: isSelected, displayText, id, imageId, onClickAction, highZoomMaxWidth }) => {
  const onKeyDownHandler = (e, id) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClickAction(id);
    }
  };

  return (
    <li onClick={() => onClickAction(id)}>
      <DivAsCheckbox
        data-testid={id}
        role="checkbox"
        aria-label={displayText}
        aria-checked={isSelected}
        onKeyDown={e => onKeyDownHandler(e, id)}
        tabIndex={0}
        $highZoomMaxWidth={highZoomMaxWidth}
      >
        <StyledCheckboxWrapper>
          <CheckIconWrapper $selected={isSelected} $highZoomMaxWidth={highZoomMaxWidth}>
            {isSelected && <Icon name="tick" cdn iconSize="sm" wrapper="span" />}
          </CheckIconWrapper>
        </StyledCheckboxWrapper>
        {imageId ? (
          <ImageIconWrapper>
            <Icon name={imageId} cdn width="auto" height="auto" wrapper="span" />
          </ImageIconWrapper>
        ) : (
          displayText
        )}
      </DivAsCheckbox>
    </li>
  );
};

const PrimaryLabelStyled = styled(PrimaryLabel)`
  margin: 16px 0;
  && {
    font-weight: 500;
  }
  .-desktop & {
    margin: 28px 0 16px;
  }
`;
const StyledGroup = styled.div`
  width: 100%;
`;
const LaneTypePickerUL = styled.ul`
  margin: 16px 0;
  padding: unset;
  box-shadow: none;
  list-style-type: none;
`;

const StepView = ({ title, subtitle, categories, onCategoryClick, highZoomMaxWidth }) => (
  <>
    <PrimaryLabelStyled data-testid="LanePickerTitle" id="ModalBoxTitle">
      {title}
    </PrimaryLabelStyled>
    <SecondaryLabel>{subtitle}</SecondaryLabel>
    <StyledGroup role="group" aria-label="security lane choices">
      <LaneTypePickerUL>
        {categories &&
          categories.map(type => (
            <LaneTypeArea key={type.id} {...type} onClickAction={onCategoryClick} highZoomMaxWidth={highZoomMaxWidth} />
          ))}
      </LaneTypePickerUL>
    </StyledGroup>
  </>
);

export default StepView;
